import { effect, inject, Injectable, untracked } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { loginMessageCompany, loginMessagePerson } from '../../config/login-message';
import { AuthService, CustomerService, InsuranceTypesService, InsurenetInterfaceService } from '@core';
import { customerToOrderState } from '@models/customer';
import { sendConversionEvent, sendGAEvent } from '../../utils/google-analytics';
import { PriceService } from '../price/price.service';
import { OrderQuery } from './order.query';
import { OrderStore } from './order.store';
import { OrderState } from '../models/order-state.model';
import { ProductsService } from '../../products/products.service';
import { Insurant } from '@models/insurant';
import { toSignal } from '@angular/core/rxjs-interop';
import { OrderStepsService } from '../order-step.service';
import { Tag } from '@models/tag';
import { Product } from '@models/product';

/**
 * @description handles most of the order forms interaction with the store: updating store,
 *  traversing form steps, submitting the order, setting new product for an order.
 *  Components should never interact with the store directly. If the store needs to be
 *  updated this should always be handled through OrderService. OrderQuery will handle
 *  readonly access to the stores state.
 * @see OrderStore
 * @see OrderQuery
 */
@Injectable({ providedIn: 'root' })
export class OrderService {
    private productService = inject(ProductsService);
    private product = this.productService.product;
    private insuranceType = inject(InsuranceTypesService).insuranceType;
    private query = inject(OrderQuery);
    private orderStepService = inject(OrderStepsService);
    private state = toSignal(this.query.state$);
    private _requestPending$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public requestPending$: Observable<boolean> = this._requestPending$.asObservable();

    public constructor(
        private authService: AuthService,
        private orderStore: OrderStore,
        private customerService: CustomerService,
        private insurenetInterface: InsurenetInterfaceService,
        private priceService: PriceService,
    ) {
        this.subscribeToCustomerChange();

        // Update the store when the product change
        effect(() => {
            const newProduct = this.product();

            if (typeof newProduct?.onInit === 'function') {
                untracked(() => this.update(newProduct.onInit(this.state(), newProduct)));
            }
        });
    }

    /**
     * @description set insuranceType and product and resets the store.
     *  Will do nothing if current and new product is the same.
     * @param {Pick<OrderState, 'insuranceType' | 'product'>} state a partial state object
     *  only comprised of insuranceType and product. Both have to be set at the same time.
     * @default {}
     */
    public setProduct(product: Product): void {
        if (this.productService.baseProduct() === this.productService.getBaseBySlug(product.slug)) {
            // base product is the same, keep store as is:
            return;
        }
        this.orderStore.reinit(product, this.customerService.customer);
        this.priceService.resetPrice();
    }

    /**
     * @description call to the next step in the order form.
     * @param {Partial<OrderState>} state
     */
    public next(state: Partial<OrderState>): void {
        this.orderStore.update({ ...state });

        const nextStep = this.orderStepService.traverseSteps();
        if (nextStep !== null) {
            // send ga event:
            sendGAEvent(
                this.orderStepService.currentStep() || 'start',
                'produkt_bestellung',
                this.insuranceType().name,
            );
            this.orderStepService.setStep(nextStep, true);
        }
    }

    /**
     * @description go back to the last step in the order form.
     */
    public prev(state?: Partial<OrderState>): void {
        const prevStep = this.orderStepService.traverseSteps(-1);
        if (prevStep === '' && state) {
            this.orderStore.update({ ...state });
        }
        if (prevStep !== null) {
            this.orderStepService.setStep(prevStep);
        }
    }

    /**
     * @description update order store with state
     * @param {Partial<OrderState>} state
     */
    public update(state: Partial<OrderState>): void {
        this.orderStore.update({ ...state });
    }

    /**
     * Updates the valid status of a step
     * @param step The step slug that should be flagged
     * @param isInvalid
     */
    public setValidState(step: string, isInvalid: boolean): void {
        let invalidSteps = this.orderStore.getValue().invalidSteps;

        if (isInvalid) {
            invalidSteps = Array.from(new Set(invalidSteps).add(step));
        } else {
            invalidSteps = invalidSteps.filter((item) => item !== step);
        }
        this.orderStore.update({ invalidSteps });
    }

    /**
     * @description close order by submitting as a contract request.
     * @param {Partial<OrderState>} state
     * @returns {Observable<unknown>}
     */
    public submit(state: Partial<OrderState>): Observable<unknown> {
        this.orderStore.update({ ...state });
        this._requestPending$.next(true);
        const order = this.orderStore.getValue();
        return this.insurenetInterface
            .submitContractRequest(order, this.product(), this.insuranceType(), this.customerService.customer)
            .pipe(
                tap(() => {
                    // send ga event:
                    sendGAEvent('abschluss', 'produkt_bestellung', this.insuranceType().name);
                    // send conversion event:
                    const price =
                        this.priceService.price() * (this.insuranceType().settlementPeriod === 'month' ? 12 : 1);
                    sendConversionEvent(price);
                }),
                finalize(() => this._requestPending$.next(false)),
            );
    }

    /**
     * @description add the tag to the current orderState with the corresponding value.
     * If the tag is already present, it updates the value instead
     * @param {Tag} key the key of the tag
     * @param {unknown} value the value for the tag
     * @returns {void}
     */
    public setTag(key: Tag, value: boolean): void {
        const tags = new Map(this.orderStore.getValue().tags);
        tags.set(key, value);
        this.orderStore.update({ tags: tags });
    }

    /**
     * @description like {@link setTag}, but allows setting multiple tags at once.
     * @param {Partial<Record<Tag, any>>} tags the tags to set as an object of key/value pairs.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public setTags(tags: Partial<Record<Tag, any>>): void {
        const next = new Map(this.orderStore.getValue().tags);
        Object.keys(tags).forEach((key) => next.set(key as Tag, tags[key]));
        this.orderStore.update({ tags: next });
    }

    /**
     * @description get the value for the specified tag.
     * @param {Tag} key the key of the tag
     * @returns {unknown} the value of the tag
     */
    public getTag(key: Tag): unknown {
        return this.orderStore.getValue().tags.get(key);
    }

    /**
     * @description remove a tag from the current orderState
     * @param {Tag} key the key of the tag
     * @returns {void}
     */
    public removeTag(key: Tag): void {
        const tags = new Map(this.orderStore.getValue().tags);
        tags.delete(key);
        this.orderStore.update({ tags: tags });
    }

    /**
     * @description handles customers logging in or out during use of the
     *  order form.
     */
    private subscribeToCustomerChange(): void {
        this.customerService.customer$.subscribe((customer) => {
            if (customer) {
                // if the customer is a natural person but only companies are allowed
                // or the customer is a company but only natural persons are allowd
                // reinit the order form with product, insuranceType and customer only.
                // customer will not be allowed to proceed then.
                if (
                    (customer.title.id === environment.titleCompanyId &&
                        this.product()?.orderConfig.naturalPersonsOnly) ||
                    (customer.title.id !== environment.titleCompanyId && this.product()?.orderConfig.companiesOnly)
                ) {
                    if (
                        confirm(
                            customer.title.id === environment.titleCompanyId ? loginMessageCompany : loginMessagePerson,
                        )
                    ) {
                        this.orderStore.reinit(this.product(), customer);
                    } else {
                        this.authService.logout();
                    }
                } else {
                    const customerData = customerToOrderState(customer);
                    const { firstName, lastName, dateOfBirth } = customerData.billingAddress;
                    const insurants: Insurant[] | undefined = this.orderStore.getValue().insurants;
                    if (dateOfBirth && insurants?.length) {
                        const updatedInsurants: Insurant[] = [];
                        insurants.forEach((insurant) => {
                            if (insurant.isInsuree) {
                                updatedInsurants.push({ ...insurant, dateOfBirth, firstName, lastName });
                            } else {
                                updatedInsurants.push(insurant);
                            }
                        });
                        this.orderStore.update({ insurants: updatedInsurants, ...customerData });
                    } else {
                        this.orderStore.update({
                            ...customerData,
                        });
                    }
                }
            } else {
                // when customer logs out remove customer data and remove
                // billing address and payment data from completed steps:
                this.orderStore.update({
                    billingAddress: {},
                    payment: {},
                    completedSteps: this.orderStore
                        .getValue()
                        .completedSteps.filter((step) => step !== 'rechnungsadresse' && step !== 'zahlungsdaten'),
                });
            }
        });
    }
}
